import { useEffect, useMemo, useState } from "react";
import { Mail, Phone, UserPlus, X } from "lucide-react";
import Button from "../ui/Button";
import Select from "../ui/Select";
import { ROLES } from "../../config/roles";
import usePermissions from "../../hooks/usePermissions";
import { getPermissionLabel } from "../../config/permissions";
import {
  getRoleApiSlug,
  getRoleDisplayLabel,
  getRolePermissions,
  listAssignableRoles,
  subscribeRolePermissions,
} from "../../data/opsRolePermissionsStore";

export default function InviteOpsUserModal({ onClose, onInvite }) {
  const { role: actorRole } = usePermissions();
  const [roles, setRoles] = useState(() => listAssignableRoles({ actorRole }));
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleName, setRoleName] = useState(ROLES.CALL_CENTER_AGENT);
  const [submitting, setSubmitting] = useState(false);

  const permissions = useMemo(() => getRolePermissions(roleName), [roleName]);
  const roleUuid = getRoleApiSlug(roleName);

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: r,
        label: getRoleDisplayLabel(r),
        description: getRoleApiSlug(r)
          ? `${getRolePermissions(r).length} permissions`
          : "Role UUID missing — reload roles",
        disabled: !getRoleApiSlug(r),
      })),
    [roles]
  );

  useEffect(() => {
    return subscribeRolePermissions(() => setRoles(listAssignableRoles({ actorRole })));
  }, [actorRole]);

  useEffect(() => {
    if (!roles.includes(roleName) && roles.length) {
      setRoleName(roles[0]);
    }
  }, [roles, roleName]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, submitting]);

  const canSubmit =
    firstName.trim() &&
    email.trim() &&
    phone.trim() &&
    roleName &&
    Boolean(roleUuid) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const apiRoleSlug = getRoleApiSlug(roleName);
    if (!apiRoleSlug) {
      return;
    }
    setSubmitting(true);
    try {
      await onInvite({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone_number: phone.trim(),
        role_slug: apiRoleSlug,
        role: roleName,
      });
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        role="dialog"
        aria-labelledby="invite-ops-user-title"
        className="flex max-h-[min(94vh,900px)] w-full max-w-2xl flex-col overflow-visible rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/55">
                <UserPlus className="h-3 w-3" />
                Team member
              </span>
              <h2 id="invite-ops-user-title" className="mt-2 text-lg font-bold text-primary">
                Onboard team member
              </h2>
              <p className="mt-1 text-sm text-primary/45">
                Enter name, email, phone, and role. A welcome email is sent after registration.
              </p>
            </div>
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              className="rounded-lg p-2 text-primary/40 hover:bg-primary/5 disabled:opacity-40"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-primary/50">First name</label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Abbey"
                  disabled={submitting}
                  className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-primary/50">
                  Last name <span className="font-medium text-primary/35">(optional)</span>
                </label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Sevo"
                  disabled={submitting}
                  className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-primary/50">Work email</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/30" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="agent@simcollect.gh"
                    disabled={submitting}
                    className="w-full rounded-lg border border-primary/10 py-2.5 pl-10 pr-3 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-primary/50">Phone number</label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/30" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="233553261251"
                    disabled={submitting}
                    className="w-full rounded-lg border border-primary/10 py-2.5 pl-10 pr-3 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
                  />
                </div>
              </div>
            </div>

            <Select
              label="Role"
              value={roleName}
              onChange={setRoleName}
              options={roleOptions}
              disabled={submitting}
            />

            <div>
              <p className="mb-1.5 text-xs font-semibold text-primary/50">
                Permissions inherited ({permissions.length})
              </p>
              <div className="flex max-h-36 flex-wrap gap-1.5 overflow-y-auto rounded-xl border border-primary/[0.07] bg-primary/[0.02] p-2.5">
                {permissions.length ? (
                  permissions.map((key) => (
                    <span
                      key={key}
                      title={key}
                      className="rounded-md bg-white px-2 py-1 text-[10px] font-semibold text-primary/70 ring-1 ring-primary/[0.08]"
                    >
                      {getPermissionLabel(key)}
                    </span>
                  ))
                ) : (
                  <span className="text-[11px] text-primary/40">No permissions on this role.</span>
                )}
              </div>
              {roleUuid ? (
                <p className="mt-2 text-[11px] text-primary/40">
                  Role sent as <span className="font-semibold text-primary">{roleUuid}</span>
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-primary/[0.06] px-5 py-4 sm:px-6">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Onboarding…" : "Onboard member"}
          </Button>
        </div>
      </div>
    </div>
  );
}
